
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const getNavItems = () => {
    if (location.pathname === '/obrigado-espera-copy') {
      return [
        { name: 'Home', href: '/' }
      ];
    }
    
    if (location.pathname === '/duocore') {
      return [
        { name: 'Home', href: '/' },
        { name: 'Planos', href: '#planos-duocore' },
        { name: 'Resultados', href: '#resultados-duocore' },
        { name: 'Como Funciona', href: '#como-funciona' },
        { name: 'Dúvidas Frequentes', href: '#faq' }
      ];
    }
    
    return [
      { name: 'Produtos', href: '#products' },
      { name: 'Copy Trade', href: '#products' },
      { name: 'Planos', href: '#products' },
      { name: 'Resultados', href: '#testimonials' },
      { name: 'Como Funciona', href: '#benefits' },
      { name: 'Dúvidas Frequentes', href: '#faq' }
    ];
  };

  const navItems = getNavItems();

  const handleNavigation = (href: string) => {
    if (href.startsWith('/')) {
      // É uma navegação para outra página
      navigate(href);
    } else {
      // É um scroll para seção
      const element = document.querySelector(href);
      if (element) {
        const headerHeight = 80; // Altura do header em pixels
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset - headerHeight;
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      }
    }
    setIsMenuOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <header className="bg-slate-900/95 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center justify-center md:justify-start flex-1 md:flex-initial">
            <button 
              onClick={scrollToTop}
              aria-label="Voltar ao topo da página QuantMake"
              className="w-40 h-40 flex items-center justify-center p-2 cursor-pointer hover:opacity-80 transition-opacity duration-200 focus:outline-none rounded-lg"
            >
              <img 
                src="/lovable-uploads/quantmake-logo-principal-robo-trader-automatizado.png" 
                alt="QuantMake.AI - Logo principal robô trader automatizado e copy trade inteligente para day trade"
                className="w-full h-full object-contain" 
                title="QuantMake.AI - Sistemas de Trading Automatizado"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6 lg:space-x-8">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.href)}
                className="text-slate-300 hover:text-cyan-400 transition-colors duration-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-2 py-1"
              >
                {item.name}
              </button>
            ))}
          </nav>

          {/* Desktop Button */}
          <div className="hidden md:flex items-center">
            <a href="https://rebrand.ly/AutoTradingAcademy" target="_blank" rel="noopener noreferrer" title="Acessar área exclusiva do cliente QuantMake">
              <Button className="bg-gradient-to-r from-cyan-400 to-green-400 hover:from-cyan-500 hover:to-green-500 text-slate-900 font-semibold focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900">
                Área do Cliente
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden absolute right-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={isMenuOpen}
              className="text-slate-300 focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-slate-800 rounded-lg mt-2">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href)}
                  className="block w-full text-left px-3 py-2 text-slate-300 hover:text-cyan-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-800 rounded"
                >
                  {item.name}
                </button>
              ))}
              <div className="pt-4">
                <a href="https://rebrand.ly/AutoTradingAcademy" target="_blank" rel="noopener noreferrer" title="Acessar área exclusiva do cliente QuantMake" className="block">
                  <Button className="w-full bg-gradient-to-r from-cyan-400 to-green-400 hover:from-cyan-500 hover:to-green-500 text-slate-900 font-semibold focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-800">
                    Área do Cliente
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
